/**
 * Camera Raw 式基础调整（M4-08 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 七个控件，三类不同的数学，不是"曲线的近似拼凑"：
 * - 曝光/高光/阴影/白色/黑色：逐通道、不看邻域，烘成一张 256 项 LUT
 *   （`basicToneLut`）——与 `lut.ts` 的 curvesLut/levelsLut 同一形状，
 *   五个控件的权重函数各自独立（高光只咬高值区、阴影只咬低值区、
 *   白色/黑色是端点重新定义，互不覆盖）。
 * - 清晰度：局部对比度，USM 变体但半径大、阈值高（细节增强挪到大尺度，
 *   避免在皮肤纹理这种小尺度上产生光晕）。
 * - 去朦胧：以大半径局部均值估计"雾"（近似大气光），从原始值里减去
 *   一部分并放大局部对比度；这是暗通道去雾算法的简化近似，不是完整实现。
 *
 * 清晰度/去朦胧要看邻域，所以是整图（或整个合成区域）级别的函数，
 * 不是逐像素 LUT——这也是为什么 `ResolvedAdjustment` 里 'basic' 是独立
 * 一种类型而不是复用 'lut'（见 engine/adjustments/resolve.ts）。
 */
import { boxBlurAlpha } from '../effects/render';

export interface BasicParams {
  /** 曝光，EV（−5–5）。 */
  readonly exposure: number;
  /** 高光，−100–100。 */
  readonly highlights: number;
  /** 阴影，−100–100。 */
  readonly shadows: number;
  /** 白色，−100–100。 */
  readonly whites: number;
  /** 黑色，−100–100。 */
  readonly blacks: number;
  /** 清晰度（局部对比度），−100–100。 */
  readonly clarity: number;
  /** 去朦胧，−100–100。 */
  readonly dehaze: number;
}

export const DEFAULT_BASIC_PARAMS: BasicParams = {
  exposure: 0,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
  clarity: 0,
  dehaze: 0,
};

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * 曝光/高光/阴影/白色/黑色 → 256 项 LUT。逐通道独立应用（与 curvesLut 同一
 * 使用方式），通道自身的值当作它自己的"权重"——不额外算整像素的亮度，
 * 这样每个通道各自响应，更接近传感器级曝光而不是"整体亮度映射"。
 */
export function basicToneLut(params: BasicParams): Uint8Array {
  const lut = new Uint8Array(256);
  for (let v = 0; v < 256; v++) {
    let x = v / 255;

    // 曝光：线性光量乘 2^EV
    x *= Math.pow(2, params.exposure);

    // 高光：权重只在高值区显著（x² 在低值区几乎为零），压高光时
    // 越接近死白效果越弱（(1-x) 因子），避免把已经死白的像素"救"回来
    const highlightWeight = x * x;
    x += (params.highlights / 100) * highlightWeight * (1 - x) * 0.6;

    // 阴影：权重只在低值区显著，与高光对称
    const shadowWeight = (1 - x) * (1 - x);
    x += (params.shadows / 100) * shadowWeight * x * 0.6;

    // 白色/黑色：重新定义输出端点（同色阶的输入黑白场，但与上面两步复合，
    // 不是同一个线性映射的两个参数）。方向跟随 Adobe Camera Raw 的约定：
    // 白色右移（正值）扩大纯白区域（分母变小，同样输入更容易到 255）；
    // 黑色右移（正值）缩小纯黑区域（分子在暗部被抬高，阴影不再死黑）。
    const whiteShift = -(params.whites / 100) * 0.3;
    const blackShift = -(params.blacks / 100) * 0.3;
    const span = Math.max(1e-6, 1 + whiteShift - blackShift);
    x = (x - blackShift) / span;

    lut[v] = Math.round(clamp01(x) * 255);
  }
  return lut;
}

/** 单通道盒式模糊三连（近似高斯），复用 effects/render.ts 已有实现。 */
function blurChannel(channel: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  return boxBlurAlpha(channel, width, height, radius);
}

function splitChannels(rgb: Uint8Array, count: number): [Uint8Array, Uint8Array, Uint8Array] {
  const r = new Uint8Array(count);
  const g = new Uint8Array(count);
  const b = new Uint8Array(count);
  for (let i = 0; i < count; i++) {
    r[i] = rgb[i * 3]!;
    g[i] = rgb[i * 3 + 1]!;
    b[i] = rgb[i * 3 + 2]!;
  }
  return [r, g, b];
}

/**
 * 清晰度：局部对比度，USM 的变体——半径比锐化大得多（默认量级：图像短边的
 * 数个百分点）、阈值更高（避免小尺度噪声/细纹理被放大，只在大尺度结构上
 * 加对比）。输入输出都是紧凑 RGB（无 alpha）三通道交织缓冲。
 */
export function applyClarity(
  rgb: Uint8Array,
  width: number,
  height: number,
  amount: number,
): Uint8Array {
  if (amount === 0) return rgb;
  const count = width * height;
  const radius = Math.max(4, Math.round(Math.min(width, height) * 0.02));
  const threshold = 12; // 比一般 USM 的阈值（常见 2–4）高得多——只咬明显边缘
  const scale = amount / 100;

  const [r, g, b] = splitChannels(rgb, count);
  const br = blurChannel(r, width, height, radius);
  const bg = blurChannel(g, width, height, radius);
  const bb = blurChannel(b, width, height, radius);

  const out = new Uint8Array(rgb.length);
  const channels: readonly [Uint8Array, Uint8Array][] = [
    [r, br],
    [g, bg],
    [b, bb],
  ];
  for (let i = 0; i < count; i++) {
    for (let c = 0; c < 3; c++) {
      const [channel, blurred] = channels[c]!;
      const orig = channel[i]!;
      const blur = blurred[i]!;
      const diff = orig - blur;
      const value = Math.abs(diff) > threshold ? orig + diff * scale : orig;
      out[i * 3 + c] = Math.min(255, Math.max(0, Math.round(value)));
    }
  }
  return out;
}

/**
 * 去朦胧：暗通道先验去雾算法的简化近似——用大半径局部均值估计"大气光"，
 * 从原始值里减去一部分雾气分量再重新拉伸对比；负值方向反过来（往局部均值
 * 靠拢）近似"加雾"。不是完整的暗通道先验实现（没有透射率图/引导滤波），
 * 但方向与量级正确：去雾时暗部变暗、整体反差变大；加雾时发灰、反差变小。
 */
export function applyDehaze(
  rgb: Uint8Array,
  width: number,
  height: number,
  amount: number,
): Uint8Array {
  if (amount === 0) return rgb;
  const count = width * height;
  const radius = Math.max(8, Math.round(Math.min(width, height) * 0.08)); // 比清晰度更大的尺度——雾是大尺度现象
  const t = amount / 100;

  const [r, g, b] = splitChannels(rgb, count);
  const br = blurChannel(r, width, height, radius);
  const bg = blurChannel(g, width, height, radius);
  const bb = blurChannel(b, width, height, radius);

  const out = new Uint8Array(rgb.length);
  const channels: readonly [Uint8Array, Uint8Array][] = [
    [r, br],
    [g, bg],
    [b, bb],
  ];
  // 去雾（t>0）：把局部均值（近似大气光）当作要压低的"雾气基线"，
  // 用它把原始值往外拉伸；加雾（t<0）：原始值往局部均值收缩
  const strength = t > 0 ? t * 1.5 : t; // 去雾方向效果更明显，加雾方向保守（避免整图糊成一片灰）
  for (let i = 0; i < count; i++) {
    for (let c = 0; c < 3; c++) {
      const [channel, blurred] = channels[c]!;
      const orig = channel[i]!;
      const blur = blurred[i]!;
      const value = blur + (orig - blur) * (1 + strength);
      out[i * 3 + c] = Math.min(255, Math.max(0, Math.round(value)));
    }
  }
  return out;
}

/** 清晰度→去朦胧依次应用（Camera Raw 的处理顺序：先局部对比度，再去雾）。 */
export function applyClarityAndDehaze(
  rgb: Uint8Array,
  width: number,
  height: number,
  clarity: number,
  dehaze: number,
): Uint8Array {
  let out = applyClarity(rgb, width, height, clarity);
  out = applyDehaze(out, width, height, dehaze);
  return out;
}
